import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  List,
  ListItem,
  TextField,
  H5,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../hooks/useNotifications'
import { getPersonOwnership, makePersonLiving } from '../../services/personService'
import getUserSummary, { getCisIdAndContributorIdPair } from '../../services/userService'
import { isAlphaNumericCisId, isAlphaNumericTreeId, isWKC } from '../../util/EntityUtil'
import { capitalizeFirstLetter, getIdElement } from '../../util/StringUtil'
import { PersonContext } from '../person/PersonContext'
import PersonOwnerPicker from '../common/PersonOwnerPicker'
import { getDmcTree } from '../../services/treeService'

export default function MakeLivingPersonDialogOverlay({ ...overlay }) {
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const { t } = useTranslation()
  const notify = useNotifications()
  const initialState = {
    input: '',
    ownerCisId: '',
    ownerTreeId: '',
    ownerName: '',
    ownerTreeType: '',
    idType: '',
    reasoning: [],
  }
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)

  const reducer = (_, currentState) => JSON.parse(JSON.stringify(currentState))
  const [state, updateState] = useReducer(reducer, initialState)
  const actions = {
    renderState: () => {
      updateState(state)
    },
    setInput: (input) => {
      state.input = input
      actions.renderState()
    },
    setOwnerCisId: (ownerCisId) => {
      state.ownerCisId = ownerCisId
      actions.renderState()
    },
    setOwnerTreeId: (ownerTreeId) => {
      state.ownerTreeId = ownerTreeId
      actions.renderState()
    },
    setOwnerName: (ownerName) => {
      state.ownerName = ownerName
      actions.renderState()
    },
    setOwnerTreeType: (ownerTreeType) => {
      state.ownerTreeType = ownerTreeType
      actions.renderState()
    },
    setIdType: (idType) => {
      state.idType = idType
      actions.renderState()
    },
    setOwner: (owner) => {
      updateState(owner)
    },
    setReasoning: (newReasoning) => {
      if (!newReasoning || !newReasoning.line || newReasoning.line.length === 0) {
        newReasoning = { line: 'NO MESSAGE' }
      }
      state.reasoning.push({ ...newReasoning, lineNumber: state.reasoning.length + 1 })
      actions.renderState()
    },
    resetReasoning: () => {
      state.reasoning = []
      actions.renderState()
    },
    onClickSave: () => {
      overlay.close()
      makePersonLiving(
        currentPerson.id,
        state.idType === 'cisId' ? state.ownerCisId : null,
        state.idType === 'treeId' ? state.ownerTreeId : null,
        reason
      )
        .then(() => {
          fetchCurrentPerson()
        })
        .catch(({ response }) => {
          response.data.notifications.forEach((notification) => {
            let msg = notification.msg
            const start = msg.indexOf('[')
            if (start > 0) {
              const end = msg.indexOf(']', start)
              if (end > 0) {
                msg = JSON.parse(msg.substring(start + 1, end))
                if (msg.error) {
                  msg = capitalizeFirstLetter(msg.error)
                }
              }
            }
            notify[notification.level](msg)
          })
        })
    },
    onClickCancel: () => {
      overlay.close()
    },
  }
  const personId = currentPerson.id
  const cisIdItem = currentPerson.metadata?.items?.find((item) => item.field === 'cisId')
  const currentPersonCisId = cisIdItem?.value.value
  const focusElementRef = useRef()

  const pickContributor = async (cisId, successMessage, failureMessage) => {
    try {
      const idResponse = await getCisIdAndContributorIdPair(
        currentPersonCisId ? `cis.user.${currentPersonCisId}` : cisId,
        'cisId'
      )
      const idPair = idResponse.data
      if (!isWKC(idPair.contributorId)) {
        const userResponse = await getUserSummary(idPair.contributorId)
        const user = userResponse.data
        // Remove the username check for just when using the creatorCisId.
        // The creatorCisId could be a legacy account that doesn't have a username and enforcing a
        // username in this specific case is disqualifying creatorCisIds that should be used
        if (idPair.cisId) {
          actions.setReasoning({ line: successMessage })
          actions.setOwnerCisId(idPair.cisId)
          actions.setInput(idPair.cisId)
          actions.setOwnerName(user?.dmcUser?.contactName || t('contactNameNotAvailable', 'Contact name not available'))
          actions.setIdType('cisId')
          return true
        }
        actions.setReasoning({
          line: `${failureMessage}: contributor has no username: ${JSON.stringify(user, null, 2)}`,
        })
        return false
      }
      actions.setReasoning({ line: `${failureMessage}: contributor (${idPair.contributorId}) is a WKC` })
      return false
    } catch (err) {
      actions.setReasoning({ line: `Error finding cisId: ${JSON.stringify(err, null, 2)}` })
      return false
    }
  }

  const checkCreator = async (personOwnership) => {
    if (personOwnership.creatorCisId) {
      return pickContributor(
        personOwnership.creatorCisId,
        `  1.a not wkc, is creator. You are the chosen one: ${personOwnership.creatorCisId}`,
        '  1.a creatorCisId is not valid'
      )
    }
    actions.setReasoning({ line: `1. creatorCisId is empty` })
    return false
  }

  const checkCisIdsList = async (cisIds, reasoningPrefix) => {
    if (cisIds && cisIds.length > 0) {
      actions.setReasoning({ line: reasoningPrefix })

      // Process each CIS ID sequentially until we find a valid one
      let found = false
      // eslint-disable-next-line no-restricted-syntax
      for (const contributorCisId of cisIds) {
        if (!found) {
          // eslint-disable-next-line no-await-in-loop
          found = await pickContributor(
            contributorCisId,
            `  ${reasoningPrefix} not wkc, has username. You are the chosen one: ${contributorCisId}`,
            `  ${reasoningPrefix} ${contributorCisId} is not valid`
          )
        }
      }
      return found
    }
    actions.setReasoning({ line: `  ${reasoningPrefix} list is empty` })
    return false
  }

  const pickTree = async (treeId, successMessage) => {
    try {
      actions.setOwnerTreeId(treeId)
      actions.setIdType('treeId')
      const dmcTree = await getDmcTree(treeId)
      actions.setOwnerName(dmcTree.data.tree.name)
      actions.setOwnerTreeType(dmcTree.data.treeType)
      actions.setInput(treeId)
      actions.setReasoning({ line: successMessage })
      return true
    } catch (err) {
      actions.setReasoning({ line: `error getting tree info: ${JSON.stringify(err, null, 2)}` })
      return false
    }
  }

  const checkTreeList = async (treeIds, reasoningPrefix) => {
    if (treeIds && treeIds.length > 0) {
      actions.setReasoning({ line: reasoningPrefix })
      let found = false
      // eslint-disable-next-line no-restricted-syntax
      for (const treeId of treeIds) {
        if (!found) {
          // eslint-disable-next-line no-await-in-loop
          found = await pickTree(treeId, `${reasoningPrefix} You are the chosen one: ${treeId}`)
        }
      }
      return found
    }
    actions.setReasoning({ line: `${reasoningPrefix} list is empty` })
    return false
  }

  const findContributor = async (pid) => {
    actions.resetReasoning()
    setLoading(true)
    const personOwnership = await getPersonOwnership(pid)
    /*
          1. creatorCisId OR creatorTreeId
            a.  For creatorCisID, if not wkca, proc, other accounts to exclude, 
                or legacy (i.e., doesn’t have a username))
          2.  oneHopOwnerCisIds OR oneHopTreeIds
            a.  For oneHopOwnerCisIds
              - if not wkca, proc, other accounts to exclude, or legacy (i.e., doesn’t have a username)
              - If multiple listed, top takes priority
            b.  For oneHopTreeIds, if multiple listed, top takes priority
          3.  contributorCisIds
            a.  if not wkca, proc, other accounts to exclude, or legacy (i.e., doesn’t have a username)
            b.  If multiple listed, top takes priority
          4.  If all above fail to produce a result, then ADMIN space 
              (i.e. the actual value that will be set and returned in the ownerCisId field is "ADMIN" 
              instead of an actual cis id).
          */
    // log presonOwnership
    if (personOwnership) {
      actions.setReasoning({ line: `personOwnership: ${JSON.stringify(personOwnership, null, 2)}` })
    } else {
      actions.setReasoning({ line: 'personOwnership is null' })
    }
    // 1. creatorCisId
    let found = await checkCreator(personOwnership)
    // 1.tree creatorTreeId
    if (!found) {
      if (personOwnership.creatorTreeId) {
        found = await pickTree(
          personOwnership.creatorTreeId,
          `1. creatorTreeId. You are the chosen one: ${personOwnership.creatorTreeId}`
        )
      }
    }

    // 2.  contributorCisIds
    if (!found) {
      found = await checkCisIdsList(personOwnership?.contributorCisIds, '2.a contributorCisIds')
    }

    // 3. oneHopOwnerCisIds
    if (!found) {
      found = await checkCisIdsList(personOwnership.oneHopOwnerCisIds, '3.a oneHopOwnerCisId')
    }

    // 3. tree oneHopTreeIds
    if (!found) {
      found = await checkTreeList(personOwnership.oneHopTreeIds, '3. oneHopTreeIds')
    }

    // 4. admin
    if (!found) {
      actions.setReasoning({ line: `4 Everything is futile, use ADMIN` })
      actions.setOwnerCisId('ADMIN')
      actions.setInput('ADMIN')
      actions.setIdType('cisId')
      actions.setOwnerName('')
      actions.setOwnerTreeType('')
      found = true
    }

    setLoading(false)
    return true
  }

  const readyToSubmit = () => {
    if (state.ownerCisId) {
      if (state.ownerCisId.startsWith('cis.wkca') || state.ownerCisId.startsWith('cis.prod')) {
        isAlphaNumericCisId(getIdElement(state.ownerCisId))
      }
      return state.ownerName !== '' || state.input === 'ADMIN'
    }
    isAlphaNumericTreeId(state.ownerTreeId)
    return state.ownerName !== '' || state.input === 'ADMIN'
  }

  // Prepopulate owner Id
  useEffect(() => {
    const getContributor = async () => {
      if (overlay.isOpen) {
        await findContributor(personId)
      }
    }
    getContributor()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId, overlay.isOpen])

  // focus on reason textbox when dialog is first opened
  useEffect(() => {
    if (!overlay.isOpen && overlay.transitioning) {
      actions.setIdType('cisId')
      actions.setInput('')
      actions.setOwnerName('')
      actions.setOwnerTreeType('')
      actions.setOwnerCisId('')
      actions.setOwnerTreeId('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay.isOpen, overlay.transitioning])

  return (
    <DialogOverlay
      {...overlay}
      focusRef={focusElementRef}
      title={t('person.status.makeLiving', 'Make Living')}
      subtitle={t(
        'person.summary.makeLivingPersonDialog.subheading',
        'Are you sure you want to make this person living?'
      )}
    >
      <PersonOwnerPicker
        name="makeLivingDialogOverlay"
        textLabel={
          state.idType === 'cisId'
            ? t('person.summary.makeLivingPersonDialog.ownerCisId', 'New Owner CIS ID')
            : t('person.summary.makeLivingPersonDialog.ownerTreeId', 'New Owner Tree ID')
        }
        radioLabel={t('chooseIDType', 'Choose ID type')}
        loading={loading}
        showSpinner
        owner={state}
        updateIdType={(e) => {
          const newIdType = e.target.value
          actions.setIdType(newIdType)
          actions.setInput('')
          actions.setOwnerName('')
          actions.setOwnerTreeType('')
          actions.setOwnerCisId('')
          actions.setOwnerTreeId('')
        }}
        updateId={async (e) => {
          const newId = e.target.value
          actions.setInput(newId)
          actions.setOwnerName('')
          actions.setOwnerTreeType('')
          if (state.idType === 'cisId') {
            actions.setOwnerCisId('')
            const newIdWithoutPrefix = getIdElement(newId)
            if (
              isAlphaNumericCisId(newIdWithoutPrefix) &&
              !newId.startsWith('cis.wkca') &&
              !newId.startsWith('cis.prod')
            ) {
              try {
                setLoading(true)
                const userResponse = await getUserSummary(newIdWithoutPrefix, 'cis')
                const user = userResponse.data
                if (user?.dmcUser?.username) {
                  actions.setReasoning({ line: `Found user for ${newId}` })
                  actions.setOwnerCisId(user?.dmcUser?.cisId)
                  actions.setOwnerName(user?.dmcUser?.contactName)
                } else {
                  actions.setOwnerName('')
                  actions.setOwnerTreeType('')

                  actions.setReasoning({
                    line: `Could not find user for ${newId}`,
                  })
                }
              } catch (err) {
                actions.setOwnerName('')
                actions.setOwnerTreeType('')
                actions.setReasoning({
                  line: `Could not find user for ${newId} (${JSON.stringify(err, null, 2)})`,
                })
              }
              setLoading(false)
            } else if (newIdWithoutPrefix === 'ADMIN') {
              actions.setOwnerCisId(newIdWithoutPrefix)
            }
          } else if (state.idType === 'treeId') {
            actions.setOwnerTreeId('')
            if (isAlphaNumericTreeId(newId)) {
              try {
                setLoading(true)
                const dmcTree = await getDmcTree(newId)
                actions.setReasoning({ line: `Found tree for ${newId}` })
                actions.setOwnerTreeId(newId)
                actions.setOwnerName(dmcTree.data.tree.name)
                actions.setOwnerTreeType(dmcTree.data.treeType)
              } catch (err) {
                actions.setOwnerName('')
                actions.setOwnerTreeType('')

                actions.setReasoning({
                  line: `Could not find tree for ${newId} (${JSON.stringify(err, null, 2)})`,
                })
              }
              setLoading(false)
            }
          }
        }}
      />
      <TextField
        role="textbox"
        id="livingChangeMessage"
        label={t('reason', 'Reason')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        ref={focusElementRef}
      />
      <Expander>
        <ExpanderHeader>
          <H5>{t('makeLivingPerson.reasoning', 'Reasoning')}</H5>
        </ExpanderHeader>
        <ExpanderContent>
          <List>
            {state.reasoning.map((item) => {
              return <ListItem key={item.lineNumber} primaryText={item.line} />
            })}
          </List>
        </ExpanderContent>
      </Expander>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={actions.onClickSave} disabled={!readyToSubmit()}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={actions.onClickCancel}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
